import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LandingPage.css';

// The Fampack iOS app already exists; offered as a secondary action.
const APP_STORE_URL =
  'https://apps.apple.com/au/app/fampack-for-the-ones/id6476157901';

/* Brand "F" mark (matches /public/Logo.svg). Decorative; label lives on the link. */
function BrandMark({ className = 'fp-logo' }) {
  return (
    <svg
      className={className}
      viewBox="69.9609 66.0938 219.7271 228.0112"
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M146.187 224.121C152.38 224.121 156.224 230.857 153.075 236.189L121.97 288.862C118.874 294.105 111.288 294.105 108.192 288.862L77.0869 236.189C73.9377 230.857 77.7824 224.121 83.9756 224.121H146.187ZM269.688 145.107C280.733 145.107 289.688 154.062 289.688 165.107V195.332C289.688 206.378 280.733 215.332 269.688 215.332H89.9609C78.9152 215.332 69.9609 206.378 69.9609 195.332V165.107C69.9609 154.062 78.9152 145.107 89.9609 145.107H269.688ZM269.688 66.0938C280.733 66.0938 289.688 75.0481 289.688 86.0938V116.318C289.688 127.364 280.733 136.318 269.688 136.318H89.9609C78.9152 136.318 69.9609 127.364 69.9609 116.318V86.0938C69.9609 75.0481 78.9152 66.0938 89.9609 66.0938H269.688Z"
        fill="url(#fp-grad)"
      />
      <defs>
        <linearGradient
          id="fp-grad"
          x1="289.688"
          y1="66.0938"
          x2="74.6354"
          y2="285.771"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFAD01" />
          <stop offset="0.495192" stopColor="#FF6B00" />
          <stop offset="1" stopColor="#FFEE03" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* Simple generic robot mark to indicate Android (not the trademarked Bugdroid). */
function AndroidLogo({ className = 'fp-android__logo' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <g stroke="#3ddc84" strokeWidth="1.6" strokeLinecap="round">
        <line x1="12.5" y1="5.5" x2="10.5" y2="2.5" />
        <line x1="19.5" y1="5.5" x2="21.5" y2="2.5" />
      </g>
      <path
        d="M6 14a10 10 0 0 1 20 0v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1z"
        fill="#3ddc84"
      />
      <g fill="#ffffff">
        <circle cx="12.5" cy="13" r="1.15" />
        <circle cx="19.5" cy="13" r="1.15" />
      </g>
    </svg>
  );
}

/* Email capture that sends the applicant into the /apply flow, prefilling and
   skipping the email step when an email is provided. */
function SignupForm({ id, buttonLabel = 'Apply to Join' }) {
  const navigate = useNavigate();

  function handleSubmit(e) {
    e.preventDefault();
    const email = new FormData(e.currentTarget).get('email');
    const value = (email || '').toString().trim();
    navigate(value ? `/apply?email=${encodeURIComponent(value)}` : '/apply');
  }

  return (
    <form className="fp-signup" onSubmit={handleSubmit}>
      <div className="fp-signup__row">
        <label className="fp-visually-hidden" htmlFor={`${id}-email`}>
          Email address
        </label>
        <input
          id={`${id}-email`}
          className="fp-input"
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
        />
        <button className="fp-btn fp-btn--primary" type="submit">
          {buttonLabel}
        </button>
      </div>
    </form>
  );
}

function FeatureIcon({ name }) {
  const paths = {
    star: (
      <path d="M12 3.6l2.5 5.1 5.6.8-4.05 3.95.95 5.6L12 16.4l-5.05 2.65.95-5.6L3.85 9.5l5.6-.8z" />
    ),
    creator: (
      <>
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
        <line x1="12" y1="17.5" x2="12" y2="21" />
        <line x1="8.5" y1="21" x2="15.5" y2="21" />
      </>
    ),
    brand: (
      <>
        <rect x="3.5" y="7.5" width="17" height="12" rx="2" />
        <path d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5" />
        <line x1="3.5" y1="12.5" x2="20.5" y2="12.5" />
      </>
    ),
    free: (
      <>
        <path d="M12.6 3.4H6A2.6 2.6 0 0 0 3.4 6v6.6a2 2 0 0 0 .6 1.4l7 7a2 2 0 0 0 2.8 0l6.6-6.6a2 2 0 0 0 0-2.8l-7-7a2 2 0 0 0-1.4-.6z" />
        <circle cx="8" cy="8" r="1.3" />
      </>
    ),
  };
  return (
    <svg
      className="fp-feature__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths[name]}
    </svg>
  );
}

export default function LandingPage() {
  const year = new Date().getFullYear();

  return (
    <div className="fp" id="top">
      <a className="fp-skip" href="#main">
        Skip to content
      </a>

      {/* 1. Sticky nav */}
      <header className="fp-nav">
        <div className="fp-container fp-nav__inner">
          <a className="fp-nav__brand" href="#top" aria-label="Fampack home">
            <BrandMark />
            <span className="fp-nav__brandname">Fampack</span>
          </a>
          <nav className="fp-nav__links" aria-label="Primary">
            <a href="#how">How it works</a>
            <a href="#features">Features</a>
            <a href="#faq">FAQ</a>
          </nav>
          <Link className="fp-btn fp-btn--primary fp-btn--sm" to="/apply">
            Apply to Join
          </Link>
        </div>
      </header>

      <main id="main">
        {/* 2. Hero */}
        <section className="fp-hero">
          <div className="fp-container fp-hero__inner">
            <div className="fp-hero__copy">
              <p className="fp-eyebrow">For creators</p>
              <h1 className="fp-hero__title">
                Find and connect with your{' '}
                <span className="fp-script">
                  superfans
                  <svg
                    className="fp-underline"
                    viewBox="0 0 300 40"
                    preserveAspectRatio="none"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M5 20C100 11 200 11 295 20"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                    <path
                      d="M5 33C100 24 200 24 295 33"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                </span>
              </h1>
              <p className="fp-hero__sub">
                Fampack is a mobile platform where you build a community of your
                most dedicated fans and interact with them directly.
              </p>
              <SignupForm id="hero" />
              <div className="fp-hero__secondary">
                <a className="fp-appstore" href={APP_STORE_URL}>
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download Fampack on the App Store"
                  />
                </a>
                <Link className="fp-android" to="/android">
                  <AndroidLogo />
                  <span className="fp-android__text">
                    <strong>Android coming soon</strong>
                    <span>Click here to reserve your username</span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 3. How it works */}
        <section className="fp-section" id="how">
          <div className="fp-container">
            <h2 className="fp-section__title">How it works</h2>
            <p className="fp-section__lead">
              Three steps to start connecting with your superfans.
            </p>
            <ol className="fp-steps">
              <li className="fp-step">
                <span className="fp-step__num">1</span>
                <h3 className="fp-step__title">Apply to join</h3>
                <p className="fp-step__text">
                  Fampack is approval-only. Only approved
                  creators can join.
                </p>
              </li>
              <li className="fp-step">
                <span className="fp-step__num">2</span>
                <h3 className="fp-step__title">Create your account</h3>
                <p className="fp-step__text">
                  Once approved, sign up and set up your creator profile in a few
                  minutes.
                </p>
              </li>
              <li className="fp-step">
                <span className="fp-step__num">3</span>
                <h3 className="fp-step__title">
                  Build your community and connect
                </h3>
                <p className="fp-step__text">
                  Invite your audience, identify your superfans, and interact
                  with them directly.
                </p>
              </li>
            </ol>
          </div>
        </section>

        {/* 4. Features — the Fampack mechanism */}
        <section className="fp-section fp-section--alt" id="features">
          <div className="fp-container">
            <div className="fp-mech">
              <h2 className="fp-mech__title">Following is capped at 20</h2>
              <p className="fp-mech__text">
                Each fan can follow up to 20 creators. When someone follows
                you, they are one of your superfans.
              </p>
            </div>

            <ul className="fp-features">
              <li className="fp-feature">
                <FeatureIcon name="star" />
                <h3 className="fp-feature__title">20 follows per person</h3>
                <p className="fp-feature__text">
                  A follow means someone
                  chose you as one of their top 20.
                </p>
              </li>
              <li className="fp-feature">
                <FeatureIcon name="creator" />
                <h3 className="fp-feature__title">For creators</h3>
                <p className="fp-feature__text">
                  Your followers are your superfans. Share posts and reply to
                  them directly.
                </p>
              </li>
              <li className="fp-feature">
                <FeatureIcon name="free" />
                <h3 className="fp-feature__title">Free for both sides</h3>
                <p className="fp-feature__text">
                  Fampack is free for creators and fans. No fees, no
                  subscriptions.
                </p>
              </li>
            </ul>
          </div>
        </section>

        {/* 6. FAQ */}
        <section className="fp-section" id="faq">
          <div className="fp-container fp-container--narrow">
            <h2 className="fp-section__title">Frequently asked questions</h2>
            <div className="fp-faq">
              <details className="fp-faq__item">
                <summary>Is it free?</summary>
                <p>Yes. Fampack is free for everyone, creators and fans.</p>
              </details>
              <details className="fp-faq__item">
                <summary>What can I post?</summary>
                <p>
                  You can share photo, video, voice, and text posts with your
                  community. Sensitive or NSFW content is not allowed.
                </p>
              </details>
              <details className="fp-faq__item">
                <summary>Who is Fampack for?</summary>
                <p>
                  Fampack is for creators — musicians, artists, influencers,
                  podcasters, streamers, and writers — who want a direct way to
                  find and stay connected with their most dedicated fans.
                </p>
              </details>
              <details className="fp-faq__item">
                <summary>How do I get started as creator?</summary>
                <p>
                  Apply to join Fampack, and once approved you can start conencting with your superfans.
                </p>
              </details>
            </div>
          </div>
        </section>
      </main>

      {/* 7. Footer */}
      <footer className="fp-footer">
        <div className="fp-container fp-footer__inner">
          <div className="fp-footer__brand">
            <a className="fp-nav__brand" href="#top" aria-label="Fampack home">
              <BrandMark />
              <span className="fp-nav__brandname">Fampack</span>
            </a>
            <p className="fp-footer__tag">
              Find and connect with your superfans.
            </p>
          </div>

          <nav className="fp-footer__col" aria-label="Product">
            <h2 className="fp-footer__heading">Product</h2>
            <a href="#how">How it works</a>
            <a href="#features">Features</a>
            <a href="#faq">FAQ</a>
            <a href={APP_STORE_URL}>iOS app</a>
          </nav>

          <nav className="fp-footer__col" aria-label="Legal">
            <h2 className="fp-footer__heading">Legal</h2>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/support">Support</Link>
          </nav>

          <div className="fp-footer__col">
            <h2 className="fp-footer__heading">Contact</h2>
            <a href="mailto:team@fampack.net">team@fampack.net</a>
          </div>
        </div>
        <div className="fp-footer__legal">
          <p>© {year} Fampack. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
